import { makeObservable, observable, action, runInAction, computed, toJS } from 'mobx';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  getAllBookings,
  getBookingCounts,
  getEmployees,
  confirmBooking,
  cancelBooking,
  refundPayment
} from '../helpers/api';

// Set moment locale to Portuguese (Brazil)
moment.locale('pt-br');

class BookingsStore {
  _bookings = [];
  _employees = [];
  _bookingCountsByDate = {}; // { 'YYYY-MM-DD': count }

  // Filters
  _selectedDate = moment();
  _selectedEmployee = 'all';
  _weekStart = moment().startOf('week');

  // UI state
  _isLoading = false;
  _error = null;
  _successMessage = null;

  constructor() {
    makeObservable(this, {
      _bookings: observable,
      _employees: observable,
      _bookingCountsByDate: observable,
      _selectedDate: observable,
      _selectedEmployee: observable,
      _weekStart: observable,
      _isLoading: observable,
      _error: observable,
      _successMessage: observable,

      weekDays: computed,
      selectedEmployeeName: computed,

      loadBookings: action,
      loadEmployees: action,
      loadWeekBookingCounts: action,
      loadBookingsWithFilters: action,
      setSelectedDate: action,
      setSelectedEmployee: action,
      setWeekStart: action,
      previousWeek: action,
      nextWeek: action,
      confirmBooking: action,
      cancelBooking: action,
      refundBooking: action,
      setError: action,
      setSuccess: action,
      clearMessages: action
    });
  }

  // Getters
  get bookings() {
    // Sort by most recent created first
    const sorted = this._bookings.slice().sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
    return toJS(sorted);
  }

  get employees() {
    return toJS(this._employees);
  }

  get bookingCountsByDate() {
    return toJS(this._bookingCountsByDate);
  }

  get selectedDate() {
    return toJS(this._selectedDate);
  }

  get selectedEmployee() {
    return this._selectedEmployee;
  }

  get selectedEmployeeName() {
    if (!this._selectedEmployee || this._selectedEmployee === 'all') {
      return null;
    }
    const employee = this._employees.find(emp => emp.id === this._selectedEmployee);
    return employee?.name || 'Barbeiro';
  }

  get weekStart() {
    return toJS(this._weekStart);
  }

  get weekDays() {
    const days = [];
    const start = this._weekStart.clone();

    for (let i = 0; i < 7; i++) {
      days.push(start.clone().add(i, 'days'));
    }

    return toJS(days);
  }

  get isLoading() {
    return this._isLoading;
  }

  get error() {
    return this._error;
  }

  get successMessage() {
    return this._successMessage;
  }

  // Actions
  setError(error) {
    this._error = error;
  }

  setSuccess(message) {
    this._successMessage = message;
  }

  clearMessages() {
    this._error = null;
    this._successMessage = null;
  }

  async loadBookings(filters = {}) {
    this._isLoading = true;
    this._error = null;

    try {
      const data = await getAllBookings(filters);
      runInAction(() => {
        this._bookings = data.bookings || [];
        this._isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this._error = 'Erro ao carregar agendamentos';
        this._isLoading = false;
      });
      console.error('Failed to load bookings:', err);
    }
  }

  async loadEmployees() {
    try {
      const data = await getEmployees();
      runInAction(() => {
        this._employees = data.employees || [];
      });
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  }

  async loadWeekBookingCounts() {
    try {
      const filters = {
        from: this.weekDays[0].startOf('day').toISOString(),
        to: this.weekDays[6].endOf('day').toISOString()
      };

      if (this._selectedEmployee && this._selectedEmployee !== 'all') {
        filters.employeeId = this._selectedEmployee;
      }

      const data = await getBookingCounts(filters);

      runInAction(() => {
        this._bookingCountsByDate = data.counts || {};
      });
    } catch (err) {
      console.error('Failed to load week booking counts:', err);
    }
  }

  loadBookingsWithFilters() {
    const filters = {};

    if (this._selectedDate) {
      // Filter by selected date (entire day)
      filters.from = this._selectedDate.clone().startOf('day').toISOString();
      filters.to = this._selectedDate.clone().endOf('day').toISOString();
    }

    if (this._selectedEmployee && this._selectedEmployee !== 'all') {
      filters.employeeId = this._selectedEmployee;
    }

    this.loadBookings(filters);
  }

  setSelectedDate(date) {
    this._selectedDate = date;
    this.loadBookingsWithFilters();
  }

  setSelectedEmployee(employeeId) {
    this._selectedEmployee = employeeId;
    this.loadBookingsWithFilters();
    this.loadWeekBookingCounts(); // Reload counts when employee filter changes
  }

  setWeekStart(weekStart) {
    this._weekStart = weekStart;
  }

  previousWeek() {
    this._weekStart = this._weekStart.clone().subtract(1, 'week');
    this.loadWeekBookingCounts(); // Reload counts when week changes
  }

  nextWeek() {
    this._weekStart = this._weekStart.clone().add(1, 'week');
    this.loadWeekBookingCounts(); // Reload counts when week changes
  }

  async confirmBooking(bookingId) {
    this._isLoading = true;
    this._error = null;

    try {
      await confirmBooking(bookingId);
      runInAction(() => {
        this._successMessage = 'Agendamento confirmado com sucesso';
        this._isLoading = false;
      });
      // Reload bookings to reflect confirmation
      await this.loadBookingsWithFilters();
      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao confirmar agendamento';
        this._isLoading = false;
      });
      console.error('Failed to confirm booking:', err);
      return false;
    }
  }

  async cancelBooking(bookingId, processRefund = false) {
    this._isLoading = true;
    this._error = null;

    try {
      const result = await cancelBooking(bookingId, processRefund);
      runInAction(() => {
        const message = result.refunded
          ? 'Agendamento cancelado e reembolso processado'
          : 'Agendamento cancelado';
        this._successMessage = message;
        this._isLoading = false;
      });
      // Reload bookings to reflect cancellation
      await this.loadBookingsWithFilters();
      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao cancelar agendamento';
        this._isLoading = false;
      });
      console.error('Failed to cancel booking:', err);
      return false;
    }
  }

  async refundBooking(bookingId) {
    this._isLoading = true;
    this._error = null;

    try {
      await refundPayment(bookingId);
      runInAction(() => {
        this._successMessage = 'Reembolso processado com sucesso';
        this._isLoading = false;
      });
      // Reload bookings to reflect refund
      await this.loadBookingsWithFilters();
      return true;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Erro ao processar reembolso';
        this._isLoading = false;
      });
      console.error('Failed to refund booking:', err);
      return false;
    }
  }
}

const bookingsStore = new BookingsStore();
export default bookingsStore;